import {
  type ActivityTypeDto,
  type ApiError,
  type FleetReportDto,
  type FleetSettings,
  type MeDto,
  REPORT_CATEGORIES,
  type ReportCategory,
  type VehicleListItemDto,
} from '@bqcrm/shared';
import { Alert, Button, CloseButton, FileButton, Group, Image, Modal, NumberInput, Select, SimpleGrid, Stack, Switch, Tabs, Text, Textarea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCamera } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, HttpError } from '../../api';
import { can } from '../../auth';
import { notifyError, notifyOk } from '../../notify';

/** Żądanie multipart (pliki) z tą samą obsługą błędów co `api`. */
export async function apiForm<T>(path: string, form: FormData, method = 'POST'): Promise<T> {
  const res = await fetch(`/api${path}`, { method, body: form, credentials: 'same-origin' });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiError>;
    throw new HttpError(res.status, err.message ?? `Błąd ${res.status}`, err.details ?? [], err.data);
  }
  return data as T;
}

/** Przebieg po polsku: „123 456 km”. */
export const kmPl = (km: number | null | undefined) => (km === null || km === undefined ? '—' : `${new Intl.NumberFormat('pl-PL').format(km)} km`);

export const useFleetVehicles = (enabled = true) => useQuery({ queryKey: ['fleet', 'vehicles'], queryFn: () => api<VehicleListItemDto[]>('/fleet/vehicles'), enabled, retry: false });
export const useFleetSettings = () => useQuery({ queryKey: ['fleet', 'settings'], queryFn: () => api<FleetSettings>('/fleet/settings'), staleTime: 300_000 });
export const useActivityTypes = () => useQuery({ queryKey: ['fleet', 'activity-types'], queryFn: () => api<ActivityTypeDto[]>('/fleet/activity-types'), staleTime: 300_000 });

/** Wszystkie dane floty po zmianie (panel, listy, karta pojazdu). */
export function useFleetInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['fleet'] });
}

/** Etykieta pojazdu w listach wyboru. */
export const vehicleLabel = (v: Pick<VehicleListItemDto, 'plate' | 'make' | 'model'>) => [v.plate, [v.make, v.model].filter(Boolean).join(' ')].filter(Boolean).join(' — ');

const NAV = [
  { to: '/flota', label: 'Panel floty' },
  { to: '/flota/pojazdy', label: 'Pojazdy' },
  { to: '/flota/zgloszenia', label: 'Zgłoszenia' },
  { to: '/flota/koszty', label: 'Koszty', perm: 'flota.koszty' },
  { to: '/flota/import', label: 'Import paliwa', perm: 'flota.import' },
  { to: '/flota/ustawienia', label: 'Ustawienia floty' },
];

/** Nawigacja modułu Flota (zakładki pod nagłówkiem — podstrony mają własne adresy). */
export function FleetNav({ me }: { me: MeDto }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const items = NAV.filter((n) => !n.perm || can(me, n.perm));
  const active = [...items].reverse().find((n) => (n.to === '/flota' ? pathname === '/flota' : pathname.startsWith(n.to)))?.to ?? '/flota';
  return (
    <Tabs value={active} onChange={(v) => v && navigate(v)} mb="md">
      <Tabs.List>
        {items.map((n) => (
          <Tabs.Tab key={n.to} value={n.to}>
            {n.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
    </Tabs>
  );
}

/** Strona modułu Flota: zakładki modułu nad treścią. */
export function FleetShell({ me, children }: { me: MeDto; children: ReactNode }) {
  return (
    <>
      <FleetNav me={me} />
      {children}
    </>
  );
}

const MAX_PHOTOS = 5;

/**
 * „Zgłoś problem lub potrzebę”: kategoria, opis, „nie da się jeździć”, przebieg (opcjonalnie), do 5 zdjęć.
 * Wspólne dla zarządzającego (Zgłoszenia) i użytkownika pojazdu (Mój samochód).
 */
export function ReportFormModal({ vehicles, initialVehicleId, onClose, onDone }: { vehicles: { id: string; label: string }[]; initialVehicleId?: string | null; onClose: () => void; onDone?: (r: FleetReportDto) => void }) {
  const phone = useMediaQuery('(max-width: 48em)');
  const qc = useQueryClient();
  const [vehicleId, setVehicleId] = useState<string | null>(initialVehicleId ?? (vehicles.length === 1 ? vehicles[0]!.id : null));
  const [category, setCategory] = useState<ReportCategory>('usterka');
  const [description, setDescription] = useState('');
  const [blocksUse, setBlocksUse] = useState(false);
  const [km, setKm] = useState<number | ''>('');
  const [photos, setPhotos] = useState<File[]>([]);
  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const save = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('vehicleId', vehicleId ?? '');
      form.append('category', category);
      form.append('description', description.trim());
      form.append('blocksUse', blocksUse ? 'true' : 'false');
      if (km !== '') form.append('km', String(km));
      for (const p of photos) form.append('photos', p);
      return apiForm<FleetReportDto>('/fleet/reports', form);
    },
    onSuccess: (r) => {
      notifyOk(`Wysłano zgłoszenie ${r.number}`);
      void qc.invalidateQueries({ queryKey: ['fleet'] });
      onDone?.(r);
      onClose();
    },
    onError: notifyError,
  });
  const valid = !!vehicleId && description.trim().length >= 3;
  return (
    <Modal opened onClose={onClose} title="Zgłoszenie problemu lub potrzeby" size="lg" fullScreen={phone}>
      <Stack gap="sm">
        <Select label="Pojazd" required data={vehicles.map((v) => ({ value: v.id, label: v.label }))} value={vehicleId} onChange={setVehicleId} searchable disabled={vehicles.length === 1 && !!vehicleId} />
        <Select label="Kategoria" required data={Object.entries(REPORT_CATEGORIES).map(([value, label]) => ({ value, label }))} value={category} onChange={(v) => v && setCategory(v as ReportCategory)} allowDeselect={false} />
        <Textarea label="Opis" required placeholder="Co się dzieje, od kiedy, co jest potrzebne" autosize minRows={3} value={description} onChange={(e) => setDescription(e.currentTarget.value)} data-autofocus />
        <Switch label="Nie da się jeździć" description="Twoja ocena — zgłoszenie zostanie wyróżnione jako pilne. O wyłączeniu auta z użytkowania decyduje zarządzający flotą." checked={blocksUse} onChange={(e) => setBlocksUse(e.currentTarget.checked)} color="red" />
        <NumberInput label="Przebieg (opcjonalnie)" suffix=" km" min={0} max={5_000_000} thousandSeparator=" " allowDecimal={false} value={km} onChange={(v) => setKm(typeof v === 'number' ? v : '')} w={{ base: '100%', sm: 220 }} />
        <Stack gap={6}>
          <Group gap="xs">
            <FileButton accept="image/*" multiple onChange={(f) => setPhotos((p) => [...p, ...f].slice(0, MAX_PHOTOS))} disabled={photos.length >= MAX_PHOTOS}>
              {(props) => (
                <Button {...props} size="xs" variant="light" leftSection={<IconCamera size={14} />} disabled={photos.length >= MAX_PHOTOS}>
                  Dodaj zdjęcia
                </Button>
              )}
            </FileButton>
            <Text size="xs" c="dimmed">
              do {MAX_PHOTOS} zdjęć ({photos.length}/{MAX_PHOTOS})
            </Text>
          </Group>
          {photos.length > 0 && (
            <SimpleGrid cols={{ base: 3, sm: 5 }} spacing="xs">
              {previews.map((u, i) => (
                <div key={u} style={{ position: 'relative' }}>
                  <Image src={u} h={80} radius="sm" fit="cover" alt={photos[i]?.name} />
                  <CloseButton size="sm" pos="absolute" top={2} right={2} variant="filled" aria-label="Usuń zdjęcie" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} />
                </div>
              ))}
            </SimpleGrid>
          )}
        </Stack>
        {blocksUse && (
          <Alert color="red" variant="light" py="xs">
            Zgłoszenie trafi do zarządzającego flotą jako pilne.
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Wyślij zgłoszenie
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Wyświetlenie przypisania pojazdu (osoba / ekipa + odpowiedzialny). */
export function holderText(a: { holder: 'osoba' | 'ekipa'; label: string; responsibleName: string } | null): string {
  if (!a) return 'bez przypisania';
  return a.holder === 'ekipa' ? `${a.label} · odp. ${a.responsibleName}` : a.label;
}
